import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer, toast } from "react-toastify";
import { changeTempPasswordRequest, fetchKirtanPermission, loginRequest } from "../../services/auth.service";
import { setAuthUser, setKirtanPermission } from "../../Slice/permissionSlice";
import { landingPath } from "../../Utils/permission";

// min 8, 1 upper, 1 lower, 1 digit, 1 special (mirrors the API password policy)
const PW_RE = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/;

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [mode, setMode] = useState("login"); // "login" | "temp"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({ email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);

  // temp-password mode fields
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [tempErrors, setTempErrors] = useState({ newPassword: "", confirmPassword: "" });

  const validateLogin = () => {
    const next = { email: "", password: "" };
    if (!email) next.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(email)) next.email = "Email address is invalid";
    if (!password) next.password = "Password is required";
    setErrors(next);
    return !next.email && !next.password;
  };

  // After we have a valid session token, load the kirtan permission and route the user
  // to the first page they are allowed to open.
  const loadPermissionAndRedirect = async () => {
    try {
      const res = await fetchKirtanPermission();
      const perm = res.data?.data || {};
      dispatch(setKirtanPermission(perm));
      const target = landingPath(perm);
      if (!target) {
        toast.error("Your account has no access. Please contact an administrator.");
        sessionStorage.removeItem("token");
        return;
      }
      navigate(target);
    } catch (e) {
      toast.error("Could not load your permissions. Please try again.");
      sessionStorage.removeItem("token");
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!validateLogin()) return;

    setSubmitting(true);
    try {
      const response = await loginRequest(email, password);
      const data = response.data?.data || {};
      sessionStorage.setItem("token", data.token);
      const authUser = { user_id: data.user_id, user_name: data.user_name, email: data.email, role_id: data.role_id };
      sessionStorage.setItem("authUser", JSON.stringify(authUser));
      dispatch(setAuthUser(authUser));
      await loadPermissionAndRedirect();
    } catch (error) {
      const status = error.response?.status;
      const payload = error.response?.data;
      if (status === 307 && payload?.data?.temporary_password_token) {
        // Forced first-time / reset password change. Stash the 3-minute temp token so the
        // interceptor sends it, then switch to the temp-password form.
        sessionStorage.setItem("token", payload.data.temporary_password_token);
        setMode("temp");
        toast.info("Please set a new password to continue.");
      } else if (status === 403) {
        toast.error(payload?.message || "Too many attempts. Please try again later.");
      } else {
        toast.error(payload?.message || "Invalid email or password");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const validateTemp = () => {
    const next = { newPassword: "", confirmPassword: "" };
    if (!PW_RE.test(newPassword)) {
      next.newPassword = "Min 8 chars with upper, lower, number and special character";
    }
    if (newPassword !== confirmPassword) {
      next.confirmPassword = "Passwords do not match";
    }
    setTempErrors(next);
    return !next.newPassword && !next.confirmPassword;
  };

  const handleTempSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!validateTemp()) return;

    setSubmitting(true);
    try {
      await changeTempPasswordRequest(newPassword, confirmPassword);
      toast.success("Password set successfully. Please log in.");
      // Temp token is single-use; user logs in normally now.
      sessionStorage.removeItem("token");
      setMode("login");
      setPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      const msg = error.response?.data?.message || "Could not set password. The link may have expired — please log in again.";
      toast.error(msg);
      if (error.response?.status === 401 || error.response?.status === 498) {
        sessionStorage.removeItem("token");
        setMode("login");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={2500} hideProgressBar closeOnClick theme="colored" />
      <div className="login-container max-w-[425px] m-auto">
        {mode === "login" ? (
          <form onSubmit={handleLogin} className="login-form">
            <div className="form-group">
              <h1>Login</h1>
              <p>
                <span className="text-[#2B72DE]">Welcome Back!</span> Please login to your account
              </p>
              <label htmlFor="email" className="mt-12 text-[14px] font-[500] tracking-[0.56px]">
                Your Email
              </label>
              <input
                className=" placeholder-[13px] text-black"
                type="email"
                id="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {errors.email && <p style={{ color: "#ff5858" }}>{errors.email}</p>}
              <label htmlFor="password" className="mt-2 text-[14px] font-[500] tracking-[0.56px]">
                Password
              </label>
              <div className="relative">
                <input
                  className="placeholder-[13px] inputTextColor pr-10"
                  type={showPassword ? "text" : "password"}
                  id="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <span
                  className="absolute right-3 top-[25px] transform -translate-y-1/2 cursor-pointer"
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  {showPassword ? <i className="fa-solid fa-eye"></i> : <i className="fa-solid fa-eye-slash"></i>}
                </span>
              </div>
              {errors.password && <p style={{ color: "#ff5858" }}>{errors.password}</p>}

              <button type="submit" className="login-button" disabled={submitting}>
                {submitting ? "Please wait…" : "Login"}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleTempSubmit} className="login-form">
            <div className="form-group">
              <h1>Set New Password</h1>
              <p>Your account requires a new password before you can continue.</p>
              <label className="mt-6 text-[14px] font-[500]">New Password</label>
              <input
                className="placeholder-[13px] inputTextColor"
                type="password"
                placeholder="Minimum 8 characters"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              {tempErrors.newPassword && <p style={{ color: "#ff5858" }}>{tempErrors.newPassword}</p>}
              <label className="mt-2 text-[14px] font-[500]">Confirm Password</label>
              <input
                className="placeholder-[13px] inputTextColor"
                type="password"
                placeholder="Re-enter new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              {tempErrors.confirmPassword && <p style={{ color: "#ff5858" }}>{tempErrors.confirmPassword}</p>}
              <button type="submit" className="login-button" disabled={submitting}>
                {submitting ? "Please wait…" : "Set Password"}
              </button>
            </div>
          </form>
        )}
      </div>
    </>
  );
};

export default Login;
